import React from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useFormat } from '@loomstateorg/shared-frontend/hooks/useFormat';
import {
  DEFAULT_PRODUCT_SORT,
  PRODUCT_SORT_OPTION_DEFINITIONS,
  ProductSortOption,
} from '@loomstateorg/shared-frontend/helpers/product/ProductSort';

const SORT_SELECT_ID = 'product-sort-control';

export interface ProductSortControlProps {
  value?: ProductSortOption;
  onChange: (value: ProductSortOption) => void;
  className?: string;
  labelClassName?: string;
  selectClassName?: string;
  wrapperClassName?: string;
}

const ProductSortControl = ({
  value = DEFAULT_PRODUCT_SORT,
  onChange,
  className = '',
  labelClassName = '',
  selectClassName = '',
  wrapperClassName = '',
}: ProductSortControlProps): JSX.Element => {
  const { formatMessage } = useFormat({ name: 'product' });

  const sortByLabel = formatMessage({ id: 'sortBy', defaultMessage: 'Sort by' });

  return (
    <div className={`flex items-center gap-8 ${className}`} data-testid="sort-control">
      <label
        htmlFor={SORT_SELECT_ID}
        className={`whitespace-nowrap font-body text-14 font-regular ${labelClassName}`}
      >
        {`${sortByLabel}:`}
      </label>
      <div
        className={`relative h-40 min-w-160 rounded-lg border border-neutral-500 bg-site focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-black ${wrapperClassName}`}
      >
        <select
          id={SORT_SELECT_ID}
          data-testid="product-sort-control"
          aria-label={sortByLabel}
          value={value}
          onChange={(event) => onChange(event.target.value as ProductSortOption)}
          className={`relative z-[1] h-full w-full cursor-pointer appearance-none border-none bg-none bg-transparent py-0 pl-12 pr-32 font-body text-14 font-regular leading-loose focus:outline-none focus:ring-0 ${selectClassName}`}
        >
          {PRODUCT_SORT_OPTION_DEFINITIONS.map((option, index) => (
            <option key={option.value} value={option.value} data-testid={`sort-option-${index}`}>
              {formatMessage({ id: option.labelKey, defaultMessage: option.defaultLabel })}
            </option>
          ))}
        </select>
        <ChevronDownIcon
          className="pointer-events-none absolute right-5 top-1/2 z-0 h-20 w-30 -translate-y-1/2 stroke-1 text-primary-regular"
          aria-hidden="true"
        />
      </div>
    </div>
  );
};

export default ProductSortControl;
